import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Check, X, AlertTriangle, CalendarDays, Briefcase, Inbox, Save, Truck, FileText, Upload, Sparkles, Euro, Users, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import DragDropDocumentos from "@/components/DragDropDocumentos";
import { useTableSort } from "@/lib/useTableSort";
import { SolicitudesPortalAdmin } from "@/components/portal/SolicitudesPortalAdmin";

// Cabecera ordenable (click asc → desc → off, persistencia local)
function SortTh({ label, field, align = "left", sortBy, sortDir, requestSort, sortIcon, colSpan, width }) {
  if (!field) {
    return <th className={`text-${align} p-2.5 ${width || ""}`} colSpan={colSpan}>{label}</th>;
  }
  const active = sortBy === field;
  return (
    <th
      className={`text-${align} p-2.5 ${width || ""} cursor-pointer select-none hover:bg-zinc-800/40 transition-colors ${active ? "text-amber-300" : ""}`}
      onClick={() => requestSort(field)}
      data-testid={`th-rrhh-${field}`}
      colSpan={colSpan}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
        {label}
        <span className={`text-[9px] ${active ? "opacity-100" : "opacity-30"}`}>{sortIcon(field)}</span>
      </span>
    </th>
  );
}

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
               "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const TIPO_COLOR = {
  vacaciones: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  asuntos_propios: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  baja_medica: "bg-red-500/15 text-red-300 border-red-500/30",
  permiso_retribuido: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  sin_sueldo: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  formacion: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  festivo_trasladado: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  compensacion_horas: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30",
};

const ESTADO_COLOR = {
  pendiente: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  aprobada: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rechazada: "bg-red-500/15 text-red-300 border-red-500/30",
  cancelada: "bg-zinc-700/40 text-zinc-400 border-zinc-600",
};

export default function RRHHAdmin() {
  const { hasRole } = useAuth();
  const isReadOnly = !hasRole("admin"); // gestoria es read-only
  const [tab, setTab] = useState("bandeja");  // bandeja | calendario | saldos | festivos | documentos | nominas
  const [meta, setMeta] = useState(null);
  const [pendientes, setPendientes] = useState([]);
  const [todas, setTodas] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [festivos, setFestivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [resolveDialog, setResolveDialog] = useState(null);  // ausencia object
  const [resolveAprobar, setResolveAprobar] = useState(true);
  const [resolveComentario, setResolveComentario] = useState("");
  const [editSaldo, setEditSaldo] = useState(null);
  const [saldoForm, setSaldoForm] = useState({ saldo_vacaciones: 30, saldo_asuntos_propios: 2, arrastre_vacaciones: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, p, t] = await Promise.all([
        api.get("/rrhh/meta"),
        api.get("/rrhh/ausencias", { params: { estado: "pendiente" } }),
        api.get("/rrhh/ausencias", { params: { year } }),
      ]);
      setMeta(m.data);
      setPendientes(p.data || []);
      setTodas(t.data || []);
    } catch (err) {
      toast.error("Error cargando RRHH admin");
    } finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const loadCalendar = useCallback(async () => {
    try {
      const { data } = await api.get("/rrhh/ausencias/calendario", { params: { year, month } });
      setCalendar(data || []);
    } catch (err) { toast.error("Error calendario"); }
  }, [year, month]);
  useEffect(() => { if (tab === "calendario") loadCalendar(); }, [tab, loadCalendar]);

  const loadSaldos = useCallback(async () => {
    try {
      const { data } = await api.get("/rrhh/saldos", { params: { year } });
      setSaldos(data || []);
    } catch (err) { toast.error("Error saldos"); }
  }, [year]);
  useEffect(() => { if (tab === "saldos") loadSaldos(); }, [tab, loadSaldos]);

  const loadFestivos = useCallback(async () => {
    try {
      const { data } = await api.get("/festivos", { params: { year } });
      setFestivos(data || []);
    } catch (err) { toast.error("Error festivos"); }
  }, [year]);
  useEffect(() => { if (tab === "festivos") loadFestivos(); }, [tab, loadFestivos]);

  const openResolve = (a, aprobar) => {
    setResolveDialog(a);
    setResolveAprobar(aprobar);
    setResolveComentario("");
  };

  const resolver = async () => {
    try {
      await api.patch(`/rrhh/ausencias/${resolveDialog.id}/resolver`, {
        aprobar: resolveAprobar,
        comentario_admin: resolveComentario || null,
      });
      toast.success(resolveAprobar ? "Aprobada" : "Rechazada");
      setResolveDialog(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openEditSaldo = (s) => {
    setEditSaldo(s);
    setSaldoForm({
      saldo_vacaciones: s.saldo_vacaciones_anual,
      saldo_asuntos_propios: s.asuntos_propios_total,
      arrastre_vacaciones: s.arrastre_vacaciones,
    });
  };

  const guardarSaldo = async () => {
    try {
      await api.patch(`/rrhh/saldos/${editSaldo.user_id}?year=${year}`, saldoForm);
      toast.success("Saldo actualizado");
      setEditSaldo(null);
      loadSaldos();
    } catch (err) {
      toast.error("Error guardando saldo");
    }
  };

  return (
    <div className="p-4 lg:p-6" data-testid="rrhh-admin-page">
      <PageHeader
        title="RRHH · Administración"
        subtitle="Ausencias · Calendario equipo · Saldos · Festivos"
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-zinc-800 overflow-x-auto">
        {[
          ["bandeja", "Bandeja", pendientes.length],
          ["calendario", "Calendario equipo", 0],
          ["saldos", "Saldos", 0],
          ["festivos", "Festivos", 0],
          ["subir-ia", "Subir con IA", 0],
          ["documentos", "Documentos", 0],
          ["nominas", "Nóminas IA", 0],
        ].map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 transition whitespace-nowrap ${tab === k ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-400 hover:text-zinc-100"}`}>
            {l} {n > 0 && <span className="ml-1 bg-amber-500/20 text-amber-300 rounded-full px-1.5 py-0.5 text-[10px]">{n}</span>}
          </button>
        ))}
      </div>

      {/* Selector año (todas las tabs) */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-zinc-400">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-28" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              {[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {tab === "calendario" && (
          <div>
            <Label className="text-xs text-zinc-400">Mes</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : tab === "bandeja" ? (
        <div className="space-y-6">
          <SolicitudesPortalAdmin isReadOnly={isReadOnly} onChanged={load} />
          <BandejaTab pendientes={pendientes} todas={todas} meta={meta} onResolve={openResolve} isReadOnly={isReadOnly} />
        </div>
      ) : tab === "calendario" ? (
        <CalendarTab ausencias={calendar} year={year} month={month} meta={meta} />
      ) : tab === "saldos" ? (
        <SaldosTab saldos={saldos} onEdit={openEditSaldo} year={year} isReadOnly={isReadOnly} />
      ) : tab === "festivos" ? (
        <FestivosTab festivos={festivos} onChange={loadFestivos} isReadOnly={isReadOnly} />
      ) : tab === "documentos" ? (
        <DocumentosTab year={year} isReadOnly={isReadOnly} />
      ) : tab === "subir-ia" ? (
        isReadOnly
          ? <div className="text-sm text-zinc-500 p-8 text-center">Solo lectura (gestoría) — esta función está reservada a admin.</div>
          : <DragDropDocumentos onSavedReload={load} />
      ) : tab === "nominas" ? (
        <NominasTab year={year} isReadOnly={isReadOnly} onGoToUpload={() => setTab("subir-ia")} />
      ) : null}

      {/* Resolve dialog */}
      <Dialog open={!!resolveDialog} onOpenChange={(o) => !o && setResolveDialog(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-resolve">
          <DialogHeader>
            <DialogTitle>
              {resolveAprobar ? "Aprobar" : "Rechazar"} ausencia
            </DialogTitle>
          </DialogHeader>
          {resolveDialog && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-300">
                <b>{resolveDialog.user_nombre}</b> · {meta?.labels[resolveDialog.tipo]}
                <br />
                <span className="font-mono text-xs">{resolveDialog.fecha_inicio} → {resolveDialog.fecha_fin}</span>
                <span className="ml-2 text-zinc-400 text-xs">({resolveDialog.dias_naturales} días nat.)</span>
              </div>
              {resolveDialog.motivo && (
                <div className="text-xs text-zinc-400 bg-zinc-900/50 p-2 rounded border border-zinc-800">
                  Motivo: {resolveDialog.motivo}
                </div>
              )}
              {resolveDialog.rutas_conflicto?.length > 0 && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded p-2"
                  data-testid="rutas-conflicto">
                  <div className="flex items-center gap-1 font-medium mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Conflicto con rutas planificadas:
                  </div>
                  <ul className="text-[11px] list-disc pl-4">
                    {resolveDialog.rutas_conflicto.map((r) => (
                      <li key={r.id}><Truck className="w-3 h-3 inline mr-1" />{r.fecha} · {r.codigo || r.nombre}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <Label className="text-xs">Comentario (opcional)</Label>
                <Textarea value={resolveComentario}
                  onChange={(e) => setResolveComentario(e.target.value)}
                  placeholder={resolveAprobar ? "Visto bueno, disfruta!" : "Motivo del rechazo..."}
                  className="bg-zinc-900 border-zinc-800 min-h-[60px]" data-testid="resolve-comentario" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(null)}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={resolver}
              className={resolveAprobar ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-400" : "bg-red-500 text-zinc-50 hover:bg-red-400"}
              data-testid="resolve-confirm">
              {resolveAprobar ? <><Check className="w-4 h-4 mr-2" /> Aprobar</> : <><X className="w-4 h-4 mr-2" /> Rechazar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit saldo dialog */}
      <Dialog open={!!editSaldo} onOpenChange={(o) => !o && setEditSaldo(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-saldo">
          <DialogHeader>
            <DialogTitle>Saldo {editSaldo?.user_nombre} · {year}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Vacaciones</Label>
                <Input type="number" value={saldoForm.saldo_vacaciones}
                  onChange={(e) => setSaldoForm({ ...saldoForm, saldo_vacaciones: parseInt(e.target.value, 10) || 0 })}
                  className="bg-zinc-900 border-zinc-800" data-testid="saldo-vac" />
              </div>
              <div>
                <Label className="text-xs">Asuntos propios</Label>
                <Input type="number" value={saldoForm.saldo_asuntos_propios}
                  onChange={(e) => setSaldoForm({ ...saldoForm, saldo_asuntos_propios: parseInt(e.target.value, 10) || 0 })}
                  className="bg-zinc-900 border-zinc-800" data-testid="saldo-aapp" />
              </div>
              <div>
                <Label className="text-xs">Arrastre</Label>
                <Input type="number" value={saldoForm.arrastre_vacaciones}
                  onChange={(e) => setSaldoForm({ ...saldoForm, arrastre_vacaciones: parseInt(e.target.value, 10) || 0 })}
                  className="bg-zinc-900 border-zinc-800" data-testid="saldo-arrastre" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSaldo(null)}
              className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={guardarSaldo} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="saldo-save">
              <Save className="w-4 h-4 mr-2" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BandejaTab({ pendientes, todas, meta, onResolve, isReadOnly }) {
  return (
    <div className="space-y-6">
      <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="bandeja-pendientes">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800 flex items-center gap-2">
          <Inbox className="w-3.5 h-3.5" /> Pendientes de aprobación ({pendientes.length})
        </div>
        {pendientes.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">Sin solicitudes pendientes</div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {pendientes.map((a) => (
              <div key={a.id} className="p-3 hover:bg-zinc-900/40" data-testid={`pendiente-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-100 font-medium">{a.user_nombre}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${TIPO_COLOR[a.tipo]}`}>
                        {meta?.labels[a.tipo] || a.tipo}
                      </span>
                      <span className="text-xs text-zinc-500 font-mono">
                        {a.fecha_inicio} → {a.fecha_fin}
                      </span>
                      <span className="text-xs text-zinc-400">({a.dias_naturales}d nat / {a.dias_laborables}d lab)</span>
                    </div>
                    {a.motivo && <div className="text-xs text-zinc-400 mt-1">"{a.motivo}"</div>}
                    {a.rutas_conflicto?.length > 0 && (
                      <div className="mt-1.5 text-[11px] text-amber-300 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {a.rutas_conflicto.length} ruta(s) en conflicto
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    {isReadOnly ? (
                      <span className="text-[10px] text-zinc-500 px-2 py-1 italic">Solo lectura</span>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => onResolve(a, true)}
                          className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs"
                          data-testid={`aprobar-${a.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Aprobar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onResolve(a, false)}
                          className="bg-transparent border-red-700 text-red-300 hover:bg-red-500/10 h-7 text-xs"
                          data-testid={`rechazar-${a.id}`}>
                          <X className="w-3 h-3 mr-1" /> Rechazar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Histórico del año */}
      <HistoricoAusenciasTabla todas={todas} meta={meta} />
    </div>
  );
}

function HistoricoAusenciasTabla({ todas, meta }) {
  const { sorted, requestSort, sortIcon, sortBy, sortDir } = useTableSort(todas, { by: "fecha_inicio", dir: "desc" });
  return (
    <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="bandeja-todas">
      <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
        Histórico ({todas.length})
      </div>
      {todas.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">Sin ausencias</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <SortTh label="Empleado"        field="user_nombre"      sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Tipo"            field="tipo"             sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Desde"           field="fecha_inicio"     sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Hasta"           field="fecha_fin"        sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Días" align="right" field="dias_naturales" sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Estado"          field="estado"           sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Comentario admin" field="comentario_admin" sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((a) => (
              <tr key={a.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
                data-testid={`row-${a.id}`}>
                <td className="p-2.5 text-zinc-100">{a.user_nombre}</td>
                <td className="p-2.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${TIPO_COLOR[a.tipo]}`}>
                    {meta?.labels[a.tipo]}
                  </span>
                </td>
                <td className="p-2.5 text-zinc-300 font-mono text-xs">{a.fecha_inicio}</td>
                <td className="p-2.5 text-zinc-300 font-mono text-xs">{a.fecha_fin}</td>
                <td className="p-2.5 text-right text-zinc-200 font-mono">{a.dias_naturales}</td>
                <td className="p-2.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${ESTADO_COLOR[a.estado]}`}>
                    {a.estado}
                  </span>
                </td>
                <td className="p-2.5 text-xs text-zinc-400 max-w-[200px] truncate">
                  {a.comentario_admin || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CalendarTab({ ausencias, year, month, meta }) {
  const daysInMonth = new Date(year, month, 0).getDate();
  // Map fecha YYYY-MM-DD → lista ausencias
  const byDay = {};
  for (const a of ausencias) {
    const d1 = new Date(a.fecha_inicio + "T00:00:00");
    const d2 = new Date(a.fecha_fin + "T00:00:00");
    const cur = new Date(d1);
    while (cur <= d2) {
      if (cur.getFullYear() === year && cur.getMonth() + 1 === month) {
        const key = cur.toISOString().slice(0, 10);
        (byDay[key] = byDay[key] || []).push(a);
      }
      cur.setDate(cur.getDate() + 1);
    }
  }
  const firstDow = new Date(year, month - 1, 1).getDay();  // 0=Sun
  const offset = (firstDow + 6) % 7;  // make Mon=0
  return (
    <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg p-3" data-testid="calendar-grid">
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: offset }).map((_, i) => <div key={"e" + i} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const items = byDay[key] || [];
          const dow = new Date(year, month - 1, day).getDay();
          const isWeekend = dow === 0 || dow === 6;
          return (
            <div key={day} className={`min-h-[60px] border rounded p-1 text-xs ${isWeekend ? "border-zinc-900 bg-zinc-950/60" : "border-zinc-800 bg-zinc-950/30"}`}
              data-testid={`cal-${key}`}>
              <div className="font-mono text-[10px] text-zinc-500">{day}</div>
              {items.slice(0, 3).map((a) => (
                <div key={a.id} className={`text-[9px] truncate px-1 py-0.5 rounded mt-0.5 border ${TIPO_COLOR[a.tipo]}`}
                  title={`${a.user_nombre}: ${meta?.labels[a.tipo]}`}>
                  {a.user_nombre.split(" ")[0]}
                </div>
              ))}
              {items.length > 3 && <div className="text-[9px] text-zinc-500">+{items.length - 3}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SaldosTab({ saldos, onEdit, year, isReadOnly }) {
  const { sorted, requestSort, sortIcon, sortBy, sortDir } = useTableSort(saldos, { by: "user_nombre", dir: "asc" });
  return (
    <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="saldos-tabla">
      <table className="w-full text-sm">
        <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <tr>
            <SortTh label="Empleado"          field="user_nombre"                 sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Vacaciones anual" align="right" field="saldo_vacaciones_anual"     sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Arrastre"         align="right" field="arrastre_vacaciones"        sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Total"            align="right" field="vacaciones_total"           sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Usadas"           align="right" field="vacaciones_usadas"          sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Restantes"        align="right" field="vacaciones_restantes"       sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="AAPP rest."       align="right" field="asuntos_propios_restantes"  sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <th className="p-2.5"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={8} className="text-center py-8 text-zinc-500">Sin datos para {year}</td></tr>
          ) : sorted.map((s) => (
            <tr key={s.user_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40"
              data-testid={`saldo-row-${s.user_id}`}>
              <td className="p-2.5 text-zinc-100">{s.user_nombre}</td>
              <td className="p-2.5 text-right text-zinc-300 font-mono">{s.saldo_vacaciones_anual}</td>
              <td className="p-2.5 text-right text-zinc-300 font-mono">{s.arrastre_vacaciones}</td>
              <td className="p-2.5 text-right text-zinc-100 font-mono font-semibold">{s.vacaciones_total}</td>
              <td className="p-2.5 text-right text-amber-300 font-mono">{s.vacaciones_usadas}</td>
              <td className="p-2.5 text-right text-emerald-300 font-mono font-semibold">{s.vacaciones_restantes}</td>
              <td className="p-2.5 text-right text-sky-300 font-mono">{s.asuntos_propios_restantes}/{s.asuntos_propios_total}</td>
              <td className="p-2.5 text-right">
                {!isReadOnly && (
                  <Button size="sm" variant="ghost" onClick={() => onEdit(s)}
                    className="h-7 text-xs text-amber-300 hover:bg-amber-500/10"
                    data-testid={`edit-saldo-${s.user_id}`}>
                    Editar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FestivosTab({ festivos, onChange, isReadOnly }) {
  const [form, setForm] = useState({ fecha: "", nombre: "", tipo: "local", zona: "Burguillos" });

  const create = async () => {
    if (!form.fecha || !form.nombre) return toast.error("Fecha y nombre obligatorios");
    try {
      await api.post("/festivos", form);
      toast.success("Festivo añadido");
      setForm({ fecha: "", nombre: "", tipo: "local", zona: "Burguillos" });
      onChange();
    } catch (err) { toast.error("Error añadiendo"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Eliminar ${f.nombre}?`)) return;
    try {
      await api.delete(`/festivos/${f.id}`);
      toast.success("Eliminado");
      onChange();
    } catch (err) { toast.error("Error eliminando"); }
  };

  return (
    <div className="space-y-4" data-testid="festivos-tab">
      {/* Form alta */}
      {!isReadOnly && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-xs">Fecha</Label>
            <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
              className="bg-zinc-950 border-zinc-800 w-40" data-testid="festivo-fecha" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Nombre</Label>
            <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="bg-zinc-950 border-zinc-800" placeholder="Día de…" data-testid="festivo-nombre" />
          </div>
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-800 w-32" data-testid="festivo-tipo"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="nacional">Nacional</SelectItem>
                <SelectItem value="autonomico">Autonómico</SelectItem>
                <SelectItem value="local">Local</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Zona</Label>
            <Input value={form.zona} onChange={(e) => setForm({ ...form, zona: e.target.value })}
              className="bg-zinc-950 border-zinc-800 w-32" data-testid="festivo-zona" />
          </div>
          <Button onClick={create} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            data-testid="festivo-add">Añadir festivo</Button>
        </div>
      )}

      <FestivosTabla festivos={festivos} remove={remove} isReadOnly={isReadOnly} />
    </div>
  );
}

function FestivosTabla({ festivos, remove, isReadOnly }) {
  const { sorted, requestSort, sortIcon, sortBy, sortDir } = useTableSort(festivos, { by: "fecha", dir: "asc" });
  return (
    <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <tr>
            <SortTh label="Fecha"  field="fecha"  sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Nombre" field="nombre" sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Tipo"   field="tipo"   sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <SortTh label="Zona"   field="zona"   sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
            <th className="p-2.5"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={5} className="text-center py-8 text-zinc-500">Sin festivos</td></tr>
          ) : sorted.map((f) => (
            <tr key={f.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`festivo-${f.id}`}>
              <td className="p-2.5 font-mono text-xs text-zinc-200">{f.fecha}</td>
              <td className="p-2.5 text-zinc-100">{f.nombre}</td>
              <td className="p-2.5"><span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{f.tipo}</span></td>
              <td className="p-2.5 text-xs text-zinc-400">{f.zona || "—"}</td>
              <td className="p-2.5 text-right">
                {!isReadOnly && (
                  <Button size="sm" variant="ghost" onClick={() => remove(f)}
                    className="h-7 text-xs text-red-400 hover:bg-red-500/10"
                    data-testid={`festivo-del-${f.id}`}>
                    <X className="w-3 h-3 mr-1" /> Eliminar
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CAT_DOC_COLOR = {
  nomina: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  contrato: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  finiquito: "bg-red-500/15 text-red-300 border-red-500/30",
  certificado: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  irpf: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  otros: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
};

const CAT_DOC_LABEL = {
  nomina: "Nómina", contrato: "Contrato", finiquito: "Finiquito",
  certificado: "Certificado", irpf: "IRPF",
  alta_ss: "Alta en Seguridad Social",
  baja_ss: "Baja en Seguridad Social",
  otros: "Otros",
};

function DocumentosTab({ year, isReadOnly }) {
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [filtroUser, setFiltroUser] = useState("");
  const [filtroCat, setFiltroCat] = useState("");
  const [dialog, setDialog] = useState(false);
  const [bulkDialog, setBulkDialog] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (filtroUser) params.user_id = filtroUser;
      if (filtroCat) params.categoria = filtroCat;
      const [d, u] = await Promise.all([
        api.get("/rrhh/documentos", { params }),
        api.get("/users"),
      ]);
      setDocs(d.data || []);
      setUsers(u.data || []);
    } catch { toast.error("Error cargando documentos"); }
  }, [year, filtroUser, filtroCat]);
  useEffect(() => { load(); }, [load]);

  const remove = async (d) => {
    if (!window.confirm(`Eliminar "${d.titulo}"?`)) return;
    try {
      await api.delete(`/rrhh/documentos/${d.id}`);
      toast.success("Eliminado");
      load();
    } catch { toast.error("Error eliminando"); }
  };

  return (
    <div className="space-y-4" data-testid="documentos-tab">
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-zinc-400">Empleado</Label>
          <Select value={filtroUser || "__ALL__"} onValueChange={(v) => setFiltroUser(v === "__ALL__" ? "" : v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-48" data-testid="doc-filtro-user"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
              <SelectItem value="__ALL__">Todos</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Categoría</Label>
          <Select value={filtroCat || "__ALL__"} onValueChange={(v) => setFiltroCat(v === "__ALL__" ? "" : v)}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="doc-filtro-cat"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="__ALL__">Todas</SelectItem>
              {Object.entries(CAT_DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        {!isReadOnly && (
          <>
            <Button variant="outline" onClick={() => setBulkDialog(true)}
              className="bg-transparent border-zinc-700 text-zinc-300" data-testid="doc-bulk-open">
              <Upload className="w-4 h-4 mr-2" /> Carga masiva
            </Button>
            <Button onClick={() => setDialog(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
              data-testid="doc-add-open">
              <FileText className="w-4 h-4 mr-2" /> Subir documento
            </Button>
          </>
        )}
      </div>

      <DocumentosTabla docs={docs} users={users} isReadOnly={isReadOnly} remove={remove} />

      <DocumentoUploadDialog open={dialog} onClose={() => setDialog(false)} users={users} onSaved={load} />
      <DocumentoBulkDialog open={bulkDialog} onClose={() => setBulkDialog(false)} users={users} onSaved={load} />
    </div>
  );
}

function DocumentosTabla({ docs, users, isReadOnly, remove }) {
  // Enriquecemos cada documento con `user_name` y `categoria_label` para que
  // useTableSort pueda ordenar por texto legible (no por UUID/keys internas).
  const enriched = docs.map((d) => ({
    ...d,
    user_name: (users.find((x) => x.id === d.user_id)?.name) || d.user_id?.slice(0, 8) || "",
    categoria_label: CAT_DOC_LABEL[d.categoria] || d.categoria || "",
  }));
  const { sorted, requestSort, sortIcon, sortBy, sortDir } = useTableSort(enriched, { by: "subido_at", dir: "desc" });

  return (
    <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden">
      {sorted.length === 0 ? (
        <div className="text-center py-12 text-zinc-500" data-testid="docs-vacio">Sin documentos para los filtros aplicados</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
            <tr>
              <SortTh label="Empleado"   field="user_name"        sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Categoría"  field="categoria_label"  sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Título"     field="titulo"           sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Fecha doc." field="fecha_documento"  sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <SortTh label="Subido"     align="right" field="subido_at" sortBy={sortBy} sortDir={sortDir} requestSort={requestSort} sortIcon={sortIcon} />
              <th className="p-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`doc-${d.id}`}>
                <td className="p-2.5 text-zinc-100">{d.user_name}</td>
                <td className="p-2.5">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${CAT_DOC_COLOR[d.categoria] || CAT_DOC_COLOR.otros}`}>
                    {d.categoria_label}
                  </span>
                </td>
                <td className="p-2.5 text-zinc-200">{d.titulo}</td>
                <td className="p-2.5 font-mono text-xs text-zinc-400">{d.fecha_documento || "—"}</td>
                <td className="p-2.5 text-right font-mono text-[10px] text-zinc-500">{d.subido_at?.slice(0, 10)}</td>
                <td className="p-2.5 text-right">
                  <a href={d.url} target="_blank" rel="noopener noreferrer"
                    className="text-amber-300 hover:text-amber-200 text-xs mr-2"
                    data-testid={`doc-ver-${d.id}`}>Ver / Descargar</a>
                  {!isReadOnly && (
                    <Button size="sm" variant="ghost" onClick={() => remove(d)}
                      className="h-7 text-xs text-red-400 hover:bg-red-500/10"
                      data-testid={`doc-del-${d.id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DocumentoUploadDialog({ open, onClose, users, onSaved }) {
  const [form, setForm] = useState({ user_id: "", categoria: "nomina", titulo: "", fecha_documento: "", year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!form.user_id || !form.titulo || !file) return toast.error("Empleado, título y archivo obligatorios");
    setUploading(true);
    try {
      const { uploadToCloudinary } = await import("@/lib/cloudinary");
      const up = await uploadToCloudinary(file, "dnf/empleados", "raw");
      await api.post("/rrhh/documentos", {
        ...form,
        url: up.secure_url,
        public_id: up.public_id,
        nombre_archivo: file.name,
        tamano_bytes: up.bytes,
      });
      toast.success("Documento subido");
      onSaved();
      onClose();
      setFile(null);
      setForm({ user_id: "", categoria: "nomina", titulo: "", fecha_documento: "", year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Error subiendo");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-doc-upload">
        <DialogHeader>
          <DialogTitle>Subir documento</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Empleado</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="doc-user"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Categoría</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="doc-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {Object.entries(CAT_DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fecha doc.</Label>
              <Input type="date" value={form.fecha_documento}
                onChange={(e) => setForm({ ...form, fecha_documento: e.target.value })}
                className="bg-zinc-900 border-zinc-800" data-testid="doc-fecha" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Título</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Ej. Nómina Mayo 2026"
              className="bg-zinc-900 border-zinc-800" data-testid="doc-titulo" />
          </div>
          <div>
            <Label className="text-xs">Archivo (PDF / JPG / PNG)</Label>
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])}
              className="bg-zinc-900 border-zinc-800 file:text-zinc-300" data-testid="doc-file" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} disabled={uploading} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            data-testid="doc-submit">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</> : <><Upload className="w-4 h-4 mr-2" /> Subir</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentoBulkDialog({ open, onClose, users, onSaved }) {
  const [categoria, setCategoria] = useState("nomina");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [unmatched, setUnmatched] = useState([]);

  // Asocia archivo a empleado por nombre: nomina_juanfe.pdf → match "juanfe"
  const matchUser = (filename) => {
    const lower = filename.toLowerCase();
    return users.find((u) => lower.includes(u.email.split("@")[0].toLowerCase())
                          || lower.includes((u.name || "").toLowerCase().split(" ")[0]));
  };

  // Pre-validación: muestra antes de subir qué archivos no se podrán asociar
  React.useEffect(() => {
    if (files.length === 0) { setUnmatched([]); return; }
    setUnmatched(files.filter((f) => !matchUser(f.name)).map((f) => f.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, users]);

  // Reset completo cuando se cierra el diálogo
  const handleClose = () => {
    setFiles([]);
    setProgress({ done: 0, total: 0 });
    setUnmatched([]);
    setUploading(false);
    onClose();
  };

  const submit = async () => {
    if (files.length === 0) return toast.error("Selecciona archivos");
    if (unmatched.length === files.length) {
      return toast.error("Ningún archivo coincide con un empleado. Renombra los PDFs (ej: nomina_juanfe.pdf) o usa la pestaña Nóminas IA para un PDF combinado.", { duration: 8000 });
    }
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const { uploadToCloudinary } = await import("@/lib/cloudinary");
    const items = [];
    const failed = [];
    for (const f of files) {
      const u = matchUser(f.name);
      if (!u) { failed.push(f.name); setProgress((p) => ({ ...p, done: p.done + 1 })); continue; }
      try {
        const up = await uploadToCloudinary(f, "dnf/empleados", "raw");
        items.push({
          user_id: u.id,
          titulo: `${CAT_DOC_LABEL[categoria]} ${year}-${String(month).padStart(2, "0")} · ${u.name}`,
          url: up.secure_url,
          public_id: up.public_id,
          nombre_archivo: f.name,
          tamano_bytes: up.bytes,
        });
      } catch {
        failed.push(f.name);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    try {
      if (items.length > 0) {
        const { data } = await api.post("/rrhh/documentos/carga-masiva", {
          categoria, year, month, items,
        });
        toast.success(`${data.creados} documentos creados${failed.length ? ` (${failed.length} fallaron)` : ""}`);
        if (failed.length) {
          toast.error(`No asociados: ${failed.join(", ")}`, { duration: 6000 });
        }
        onSaved();
        handleClose();
      } else {
        toast.error("Ningún archivo pudo asociarse a un empleado");
      }
    } catch {
      toast.error("Error en carga masiva");
    } finally {
      setUploading(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-doc-bulk">
        <DialogHeader>
          <DialogTitle>Carga masiva de documentos</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Sube N archivos; se asocian por nombre. Ej: <code className="text-amber-300">nomina_juanfe.pdf</code> → Juanfe.
            <br />
            <span className="text-amber-400">Para un PDF combinado con todas las nóminas del mes, usa la pestaña <b>"Nóminas IA"</b>.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Categoría</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="bulk-cat"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {Object.entries(CAT_DOC_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Año</Label>
              <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)}
                className="bg-zinc-900 border-zinc-800" data-testid="bulk-year" />
            </div>
            <div>
              <Label className="text-xs">Mes</Label>
              <Input type="number" min={1} max={12} value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10) || month)}
                className="bg-zinc-900 border-zinc-800" data-testid="bulk-month" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Archivos (selecciona múltiples PDFs)</Label>
            <Input type="file" accept=".pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files))}
              className="bg-zinc-900 border-zinc-800 file:text-zinc-300" data-testid="bulk-files" />
            {files.length > 0 && (
              <div className="text-xs text-zinc-400 mt-1" data-testid="bulk-count">{files.length} archivo(s) seleccionado(s)</div>
            )}
            {uploading && progress.total > 0 && (
              <div className="text-xs text-amber-300 mt-1" data-testid="bulk-progress">Procesando {progress.done}/{progress.total}…</div>
            )}
            {!uploading && unmatched.length > 0 && (
              <div className="mt-2 p-2 rounded border border-red-900/50 bg-red-950/30" data-testid="bulk-unmatched">
                <div className="text-xs text-red-300 font-medium mb-1">
                  ⚠ {unmatched.length}/{files.length} archivo(s) no coinciden con ningún empleado:
                </div>
                <ul className="text-xs text-red-200/80 list-disc pl-4 space-y-0.5 max-h-24 overflow-auto">
                  {unmatched.slice(0, 10).map((n) => <li key={n}>{n}</li>)}
                  {unmatched.length > 10 && <li>… y {unmatched.length - 10} más</li>}
                </ul>
                <div className="text-[11px] text-zinc-400 mt-1">
                  Renombra los PDFs incluyendo el nombre o email del empleado (ej: <code className="text-amber-300">nomina_juanfe.pdf</code>) o usa <b>"Nóminas IA"</b> si es un PDF combinado.
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} disabled={uploading || files.length === 0 || unmatched.length === files.length}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="bulk-submit">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</> : <><Upload className="w-4 h-4 mr-2" /> Subir {files.length - unmatched.length} archivo(s)</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NominasTab({ year, isReadOnly, onGoToUpload }) {
  const [resumen, setResumen] = useState(null);
  const [nominas, setNominas] = useState([]);
  const [users, setUsers] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [month, setMonth] = useState(0);  // 0 = año completo
  const [empleadoFiltro, setEmpleadoFiltro] = useState("__ALL__");
  const [verPagadas, setVerPagadas] = useState("todas");  // todas | pagadas | pendientes
  const [expandidos, setExpandidos] = useState(new Set());
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadMonth, setUploadMonth] = useState(new Date().getMonth() + 1);
  const [reasignandoId, setReasignandoId] = useState(null);
  const [reasignDestino, setReasignDestino] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (month > 0) params.month = month;
      const [r, n, u, c] = await Promise.all([
        api.get("/rrhh/nominas/resumen-mensual", { params }),
        api.get("/rrhh/nominas", { params }),
        api.get("/users"),
        api.get("/colaboradores").catch(() => ({ data: [] })),
      ]);
      setResumen(r.data);
      setNominas(n.data || []);
      setUsers(u.data || []);
      setColaboradores(c.data || []);
      setSeleccionados(new Set());
    } catch { toast.error("Error cargando nóminas"); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  // Lista filtrada en cliente (empleado + estado de pago)
  const nominasFiltradas = useMemo(() => {
    let arr = nominas;
    if (empleadoFiltro !== "__ALL__") arr = arr.filter((n) => n.user_id === empleadoFiltro);
    if (verPagadas === "pagadas") arr = arr.filter((n) => n.pagado);
    if (verPagadas === "pendientes") arr = arr.filter((n) => !n.pagado);
    return arr;
  }, [nominas, empleadoFiltro, verPagadas]);

  // KPIs derivados del filtro activo (no del resumen del backend, que no
  // conoce los filtros de cliente — empleado/estado pago)
  const kpisFiltro = useMemo(() => {
    let bruto = 0, neto = 0, pagado = 0, pendiente = 0, npagadas = 0;
    for (const n of nominasFiltradas) {
      bruto += n.total_devengado || 0;
      neto += n.liquido_a_percibir || 0;
      if (n.pagado) {
        pagado += n.liquido_a_percibir || 0;
        npagadas += 1;
      } else {
        pendiente += n.liquido_a_percibir || 0;
      }
    }
    return { bruto, neto, pagado, pendiente, total: nominasFiltradas.length, npagadas };
  }, [nominasFiltradas]);

  // Empleados que aparecen en el filtro (para selector + agregado)
  const empleadosListado = useMemo(() => {
    const ids = new Set(nominas.map((n) => n.user_id));
    return Array.from(ids).map((id) => {
      const u = users.find((x) => x.id === id);
      return { id, name: u?.name || id.slice(0, 8), dni: u?.dni_nie };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [nominas, users]);

  // Agrupado por empleado en base al filtro de empleado + estado pagado
  const agregadoPorEmpleado = useMemo(() => {
    const by = new Map();
    for (const n of nominasFiltradas) {
      if (!by.has(n.user_id)) {
        by.set(n.user_id, { user_id: n.user_id, items: [], bruto: 0, neto: 0, pagado: 0, pendiente: 0 });
      }
      const g = by.get(n.user_id);
      g.items.push(n);
      g.bruto += n.total_devengado || 0;
      g.neto += n.liquido_a_percibir || 0;
      if (n.pagado) g.pagado += n.liquido_a_percibir || 0;
      else g.pendiente += n.liquido_a_percibir || 0;
    }
    const arr = Array.from(by.values()).map((g) => ({
      ...g,
      user: users.find((x) => x.id === g.user_id),
    }));
    arr.sort((a, b) => b.pendiente - a.pendiente || b.bruto - a.bruto);
    return arr;
  }, [nominasFiltradas, users]);

  const toggleExpandir = (uid) => {
    setExpandidos((s) => {
      const n = new Set(s);
      n.has(uid) ? n.delete(uid) : n.add(uid);
      return n;
    });
  };

  const toggleSel = (id) => {
    setSeleccionados((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleSelTodos = () => {
    if (seleccionados.size === nominasFiltradas.length) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(nominasFiltradas.map((n) => n.id)));
    }
  };

  const togglePagado = async (n) => {
    try {
      await api.patch(`/rrhh/nominas/${n.id}`, { pagado: !n.pagado });
      load();
    } catch { toast.error("Error al actualizar pago"); }
  };

  const bulkBorrar = async () => {
    const ids = Array.from(seleccionados);
    if (ids.length === 0) return;
    if (!window.confirm(`¿BORRAR ${ids.length} nómina(s)? Esta acción no se puede deshacer.`)) return;
    try {
      const { data } = await api.post("/rrhh/nominas/bulk-delete", { ids });
      toast.success(`${data.borradas} nómina(s) borrada(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al borrar"); }
  };

  const bulkPagar = async (pagado) => {
    const ids = Array.from(seleccionados);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/rrhh/nominas/bulk-pagar", { ids, pagado });
      toast.success(`${data.actualizadas} nómina(s) marcada(s) como ${pagado ? "pagadas" : "pendientes"}`);
      load();
    } catch { toast.error("Error al actualizar"); }
  };

  const reasignar = async (nominaId) => {
    if (!reasignDestino) return toast.error("Selecciona el empleado destino");
    try {
      const { data } = await api.patch(`/rrhh/nominas/${nominaId}`, { user_id: reasignDestino });
      toast.success(data.autocreated
        ? "Empleado creado y nómina reasignada"
        : "Nómina reasignada");
      setReasignandoId(null);
      setReasignDestino("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al reasignar");
    }
  };

  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const submit = async () => {
    if (!file) return toast.error("Selecciona el PDF combinado");
    setUploading(true);
    setUploadResult(null);
    setUploadProgress({ done: 0, total: 0 });
    try {
      let pdfUrl = null;
      if (file.size > 800 * 1024) {
        toast.message("Subiendo PDF a almacenamiento seguro…", { duration: 4000 });
        const { uploadToCloudinary } = await import("@/lib/cloudinary");
        const up = await uploadToCloudinary(file, "dnf/nominas-pdf", "raw");
        pdfUrl = up.secure_url;
      }
      const fd = new FormData();
      if (pdfUrl) {
        fd.append("pdf_url", pdfUrl);
      } else {
        fd.append("file", file);
      }
      fd.append("year", String(year));
      fd.append("month", String(uploadMonth));
      const { data: kickoff } = await api.post("/rrhh/nominas/parse-pdf-async", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      const jobId = kickoff.job_id;
      const startTs = Date.now();
      const MAX_MS = 30 * 60 * 1000;
      let finalData = null;
      while (Date.now() - startTs < MAX_MS) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await api.get(`/rrhh/nominas/parse-job/${jobId}`);
        if (status.progress_total) {
          setUploadProgress({ done: status.progress_done, total: status.progress_total });
        }
        if (status.status === "done") { finalData = status.result; break; }
        if (status.status === "error") throw new Error(status.error || "Error en parseo");
      }
      if (!finalData) throw new Error("Timeout esperando al parseo (más de 30 min)");
      setUploadResult(finalData);
      toast.success(`${finalData.creadas} nóminas extraídas con IA`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Error procesando PDF");
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  const remove = async (n) => {
    if (!window.confirm("Eliminar esta nómina?")) return;
    try {
      await api.delete(`/rrhh/nominas/${n.id}`);
      toast.success("Eliminada");
      load();
    } catch { toast.error("Error eliminando"); }
  };

  const allSel = seleccionados.size > 0 && seleccionados.size === nominasFiltradas.length;
  const someSel = seleccionados.size > 0 && !allSel;

  return (
    <div className="space-y-4" data-testid="nominas-tab">
      {/* Barra de filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs text-zinc-400">Mes</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="nom-mes"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="0">Todo el año</SelectItem>
              {MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Empleado</Label>
          <Select value={empleadoFiltro} onValueChange={setEmpleadoFiltro}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-64" data-testid="nom-empleado-filtro">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
              <SelectItem value="__ALL__">— Todos —</SelectItem>
              {empleadosListado.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name}{e.dni ? ` · ${e.dni}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Estado</Label>
          <Select value={verPagadas} onValueChange={setVerPagadas}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 w-40" data-testid="nom-estado-filtro"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="pagadas">Solo pagadas</SelectItem>
              <SelectItem value="pendientes">Solo pendientes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        {!isReadOnly && (
          <Button onClick={() => onGoToUpload?.()}
            variant="outline" className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900"
            data-testid="nom-subir-individual">
            <Upload className="w-4 h-4 mr-2" /> Subir nómina (PDF individual)
          </Button>
        )}
      </div>

      {/* KPIs sobre los filtros activos */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="nom-totales">
        <KPICard icon={Users} label="Nóminas filtradas" value={`${kpisFiltro.total}`} />
        <KPICard icon={Euro} label="Bruto" value={`${kpisFiltro.bruto.toFixed(2)} €`} color="emerald" />
        <KPICard icon={Euro} label="Neto" value={`${kpisFiltro.neto.toFixed(2)} €`} color="sky" />
        <KPICard icon={Euro} label={`Pagado (${kpisFiltro.npagadas})`} value={`${kpisFiltro.pagado.toFixed(2)} €`} color="emerald" />
        <KPICard icon={Euro} label={`Pendiente (${kpisFiltro.total - kpisFiltro.npagadas})`} value={`${kpisFiltro.pendiente.toFixed(2)} €`} color="amber" />
      </div>

      {/* Bulk actions */}
      {!isReadOnly && seleccionados.size > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="nom-bulk-bar">
          <span className="text-sm text-amber-200 flex-1">
            <b>{seleccionados.size}</b> nómina(s) seleccionada(s)
          </span>
          <Button size="sm" onClick={() => bulkPagar(true)}
            className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-8 text-xs"
            data-testid="nom-bulk-pagar">
            <Check className="w-3.5 h-3.5 mr-1" /> Marcar pagadas
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkPagar(false)}
            className="bg-transparent border-zinc-700 text-zinc-300 h-8 text-xs"
            data-testid="nom-bulk-pendiente">
            Marcar pendientes
          </Button>
          <Button size="sm" onClick={bulkBorrar}
            className="bg-red-500 text-white hover:bg-red-400 h-8 text-xs"
            data-testid="nom-bulk-borrar">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar {seleccionados.size}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSeleccionados(new Set())}
            className="text-zinc-500 h-8 text-xs">
            Cancelar
          </Button>
        </div>
      )}

      {/* Vista agregada por empleado (sirve también para externos con varias nóminas/mes) */}
      {agregadoPorEmpleado.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="nom-agregado">
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800 flex items-center gap-2">
            <span>Resumen por empleado</span>
            <span className="text-zinc-600">·</span>
            <span className="text-[10px] normal-case text-zinc-500">Click en una fila para expandir sus nóminas</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2.5 w-6"></th>
                <th className="text-left p-2.5">Empleado</th>
                <th className="text-right p-2.5">Nóminas</th>
                <th className="text-right p-2.5">Bruto</th>
                <th className="text-right p-2.5">Neto</th>
                <th className="text-right p-2.5">Pagado</th>
                <th className="text-right p-2.5">Pendiente</th>
              </tr>
            </thead>
            <tbody>
              {agregadoPorEmpleado.map((g) => {
                const open = expandidos.has(g.user_id);
                return (
                  <React.Fragment key={g.user_id}>
                    <tr onClick={() => toggleExpandir(g.user_id)}
                      className="border-b border-zinc-800/60 hover:bg-zinc-900/40 cursor-pointer"
                      data-testid={`nom-agregado-${g.user_id}`}>
                      <td className="p-2.5 text-zinc-500">{open ? "▾" : "▸"}</td>
                      <td className="p-2.5 text-zinc-100">{g.user?.name || g.user_id.slice(0, 8)}
                        {g.user?.dni_nie && <span className="ml-2 text-[10px] font-mono text-zinc-500">{g.user.dni_nie}</span>}
                      </td>
                      <td className="p-2.5 text-right font-mono text-zinc-300">{g.items.length}</td>
                      <td className="p-2.5 text-right font-mono text-emerald-300">{g.bruto.toFixed(2)} €</td>
                      <td className="p-2.5 text-right font-mono text-sky-300">{g.neto.toFixed(2)} €</td>
                      <td className="p-2.5 text-right font-mono text-emerald-400">{g.pagado.toFixed(2)} €</td>
                      <td className="p-2.5 text-right font-mono">
                        {g.pendiente > 0
                          ? <span className="text-amber-300 font-semibold">{g.pendiente.toFixed(2)} €</span>
                          : <span className="text-zinc-500">0,00 €</span>}
                      </td>
                    </tr>
                    {open && g.items.map((n) => (
                      <tr key={n.id} className="border-b border-zinc-900/40 bg-zinc-950/40 text-xs">
                        <td></td>
                        <td className="p-2 pl-8 text-zinc-400 font-mono">
                          {n.year}-{String(n.month).padStart(2, "0")}
                          {n.parsed_by_ai && <Sparkles className="w-3 h-3 inline ml-1 text-violet-400" />}
                        </td>
                        <td colSpan={2} className="p-2 text-right font-mono text-zinc-500">
                          {(n.total_devengado || 0).toFixed(2)} € bruto
                        </td>
                        <td className="p-2 text-right font-mono text-sky-300">{(n.liquido_a_percibir || 0).toFixed(2)} €</td>
                        <td colSpan={2} className="p-2 text-right">
                          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`nom-agg-pago-${n.id}`}>
                            <input type="checkbox" checked={!!n.pagado}
                              onChange={() => togglePagado(n)}
                              className="accent-emerald-500" />
                            <span className={n.pagado ? "text-emerald-300" : "text-amber-300"}>
                              {n.pagado ? "Pagada" : "Pendiente"}
                            </span>
                          </label>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Listado completo de nóminas individuales con selección múltiple */}
      <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="nom-listado">
        <div className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          Nóminas individuales ({nominasFiltradas.length}{nominas.length !== nominasFiltradas.length ? ` de ${nominas.length}` : ""})
        </div>
        {nominasFiltradas.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            {nominas.length === 0
              ? <>Sin nóminas en este año. Sube el PDF mensual desde la pestaña <b className="text-zinc-300">Subir con IA</b>.</>
              : <>Ningún resultado con los filtros activos. Limpia los filtros para ver más.</>}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                {!isReadOnly && (
                  <th className="p-2.5 w-8">
                    <input type="checkbox"
                      checked={allSel}
                      ref={(el) => { if (el) el.indeterminate = someSel; }}
                      onChange={toggleSelTodos}
                      className="accent-amber-500"
                      data-testid="nom-sel-todos" />
                  </th>
                )}
                <th className="text-left p-2.5">Periodo</th>
                <th className="text-left p-2.5">Empleado</th>
                <th className="text-right p-2.5">Bruto</th>
                <th className="text-right p-2.5">IRPF</th>
                <th className="text-right p-2.5">Neto</th>
                <th className="text-center p-2.5">Pagado</th>
                <th className="text-left p-2.5">Fuente</th>
                <th className="p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {nominasFiltradas.map((n) => {
                const u = users.find((x) => x.id === n.user_id);
                const sel = seleccionados.has(n.id);
                return (
                  <tr key={n.id}
                    className={`border-b border-zinc-800/60 hover:bg-zinc-900/40 ${sel ? "bg-amber-500/5" : ""}`}
                    data-testid={`nom-${n.id}`}>
                    {!isReadOnly && (
                      <td className="p-2.5">
                        <input type="checkbox" checked={sel} onChange={() => toggleSel(n.id)}
                          className="accent-amber-500" data-testid={`nom-sel-${n.id}`} />
                      </td>
                    )}
                    <td className="p-2.5 font-mono text-xs text-zinc-300">{n.year}-{String(n.month).padStart(2, "0")}</td>
                    <td className="p-2.5 text-zinc-100">{u?.name || n.user_id.slice(0, 8)}</td>
                    <td className="p-2.5 text-right font-mono text-emerald-300">{(n.total_devengado || 0).toFixed(2)} €</td>
                    <td className="p-2.5 text-right font-mono text-amber-300">{(n.irpf_retenido || 0).toFixed(2)} €</td>
                    <td className="p-2.5 text-right font-mono text-sky-300 font-semibold">{(n.liquido_a_percibir || 0).toFixed(2)} €</td>
                    <td className="p-2.5 text-center">
                      <label className="inline-flex items-center gap-1 cursor-pointer"
                        title={n.pagado && n.fecha_pago ? `Pagada · ${new Date(n.fecha_pago).toLocaleDateString("es-ES")}` : "Marcar como pagada"}>
                        <input type="checkbox" checked={!!n.pagado}
                          disabled={isReadOnly}
                          onChange={() => togglePagado(n)}
                          className="accent-emerald-500"
                          data-testid={`nom-pago-${n.id}`} />
                        <span className={`text-[10px] ${n.pagado ? "text-emerald-300" : "text-amber-300"}`}>
                          {n.pagado ? "Sí" : "No"}
                        </span>
                      </label>
                    </td>
                    <td className="p-2.5">
                      {n.parsed_by_ai && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30 inline-flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Gemini IA
                        </span>
                      )}
                    </td>
                    <td className="p-2.5 text-right">
                      {!isReadOnly && (
                        <div className="flex items-center justify-end gap-1">
                          {reasignandoId === n.id ? (
                            <>
                              <Select value={reasignDestino} onValueChange={setReasignDestino}>
                                <SelectTrigger className="bg-zinc-900 border-zinc-700 h-7 w-48 text-xs"
                                                data-testid={`nom-reasign-sel-${n.id}`}>
                                  <SelectValue placeholder="Empleado..." />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-950 border-zinc-700 text-zinc-100 max-h-72">
                                  <div className="px-2 py-1 text-[10px] uppercase text-zinc-500">Usuarios</div>
                                  {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                      {u.name} {u.dni_nie ? <span className="text-zinc-500">· {u.dni_nie}</span> : ""}
                                    </SelectItem>
                                  ))}
                                  {colaboradores.filter((c) => !c.autonomo).length > 0 && (
                                    <div className="px-2 py-1 text-[10px] uppercase text-zinc-500 border-t border-zinc-800 mt-1">
                                      Colaboradores externos (autocrear)
                                    </div>
                                  )}
                                  {colaboradores.filter((c) => !c.autonomo).map((c) => (
                                    <SelectItem key={`ce_${c.id}`} value={`colaborador_externo:${c.id}`}>
                                      {c.nombre} {c.nif ? <span className="text-zinc-500">· {c.nif}</span> : ""}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button size="sm" variant="ghost"
                                      onClick={() => reasignar(n.id)}
                                      data-testid={`nom-reasign-ok-${n.id}`}
                                      className="h-7 text-xs text-emerald-400 hover:bg-emerald-500/10">
                                ✓
                              </Button>
                              <Button size="sm" variant="ghost"
                                      onClick={() => { setReasignandoId(null); setReasignDestino(""); }}
                                      className="h-7 text-xs text-zinc-400">
                                ✕
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost"
                                      onClick={() => { setReasignandoId(n.id); setReasignDestino(n.user_id); }}
                                      data-testid={`nom-reasign-${n.id}`}
                                      title="Reasignar a otro empleado"
                                      className="h-7 text-xs text-amber-400 hover:bg-amber-500/10">
                                ↔
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => remove(n)}
                                      className="h-7 text-xs text-red-400 hover:bg-red-500/10"
                                      data-testid={`nom-del-${n.id}`}>
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={uploadDialog} onOpenChange={(o) => !o && !uploading && setUploadDialog(false)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="dialog-nom">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" /> Procesar PDF con Gemini IA
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Sube el PDF combinado de la gestoría. La IA analiza cada página y extrae bruto/neto/IRPF/SS automáticamente, asociando cada nómina al empleado por DNI/NIE.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Año del PDF</Label>
                <Input type="number" value={year} disabled className="bg-zinc-900 border-zinc-800 opacity-60" />
              </div>
              <div>
                <Label className="text-xs">Mes del PDF</Label>
                <Select value={String(uploadMonth)} onValueChange={(v) => setUploadMonth(parseInt(v, 10))}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="nom-up-mes"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">PDF combinado (N páginas = N nóminas)</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])}
                className="bg-zinc-900 border-zinc-800 file:text-zinc-300" data-testid="nom-up-file" />
              {file && <div className="text-xs text-zinc-400 mt-1">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>}
            </div>
            {uploadProgress.total > 0 && (
              <div className="text-xs text-violet-300">Procesando {uploadProgress.done}/{uploadProgress.total} páginas…</div>
            )}
            {uploadResult && (
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 text-xs space-y-1" data-testid="nom-up-result">
                <div className="text-emerald-300 font-semibold">✓ {uploadResult.creadas} nóminas creadas</div>
                {uploadResult.no_asociadas > 0 && (
                  <div className="text-amber-300">⚠ {uploadResult.no_asociadas} sin asociar (DNI no encontrado en empleados):
                    <ul className="text-[10px] mt-1 pl-3">
                      {uploadResult.detalle_no_asociadas.map((d, i) => (
                        <li key={i}>· {d.nombre} ({d.dni_nie}) — añade su DNI/NIE en Equipo</li>
                      ))}
                    </ul>
                  </div>
                )}
                {uploadResult.errores > 0 && (
                  <div className="text-red-300">✗ {uploadResult.errores} errores de IA</div>
                )}
              </div>
            )}
            <div className="text-[11px] text-violet-300/80 bg-violet-500/5 border border-violet-500/20 rounded p-2 flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>Procesamiento con <b>Gemini 2.5 Pro</b>. Puede tardar 30s–2min según el tamaño del PDF (1-2s por página).</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog(false)} disabled={uploading}
              className="bg-transparent border-zinc-700 text-zinc-300">Cerrar</Button>
            <Button onClick={submit} disabled={uploading || !file}
              className="bg-violet-500 text-zinc-950 hover:bg-violet-400" data-testid="nom-up-submit">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando…</> :
                <><Sparkles className="w-4 h-4 mr-2" /> Procesar con IA</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function KPICard({ icon: Icon, label, value, color }) {
  const txt = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    sky: "text-sky-300",
    red: "text-red-300",
  }[color] || "text-zinc-100";
  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-xl font-semibold mt-1 ${txt}`}>{value}</div>
    </div>
  );
}

